"use client";

import { complaintsData } from "@/data/complaints-data";
import { useRequestComplaints } from "@/hooks/complaints-hook";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  Send,
  ShieldAlert,
} from "lucide-react";
import { useLocale } from "next-intl";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";

type ComplaintFormData = {
  fullName: string;
  email: string;
  phone: string;
  type: string;
  subject: string;
  message: string;
};

export default function Complaints() {
  const locale = useLocale();
  const isRtl = locale === "ar";

  const [isSuccess, setIsSuccess] = useState(false);
  const [ticketNum, setTicketNum] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ComplaintFormData>();

  const { mutate, isPending } = useRequestComplaints();

  const onSubmit = async (data: ComplaintFormData) => {
    mutate(data, {
      onSuccess: (res: any) => {
        const returnedTicket =
          res?.data?.data?.ticketNumber ||
          res?.data?.ticketNumber ||
          res?.ticketNumber ||
          "";

        setTicketNum(returnedTicket);
        setIsSuccess(true);
        reset();
      },
    });
  };

  const t = complaintsData(isRtl);

  return (
    <div
      className={`min-h-screen bg-[#FDFDFD] py-12 ${isRtl ? "font-cairo rtl" : "font-sans ltr"}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-main/10 text-main mb-6"
          >
            <ShieldAlert size={32} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-500 font-medium"
          >
            {t.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, x: isRtl ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-4 lg:sticky lg:top-8"
          >
            <div className="bg-slate-50 border border-slate-100 rounded-4xl p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
                <MessageSquare className="text-main" />
                {t.mechanismTitle}
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed text-sm font-medium">
                {t.mechanismDesc}
              </p>

              <div className="space-y-6 relative">
                <div
                  className={`absolute top-8 bottom-8 w-0.5 bg-slate-200 ${isRtl ? "right-6" : "left-6"}`}
                />

                {t.steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="relative flex gap-5 items-start z-10"
                  >
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border-2 bg-white ${step.bg}`}
                    >
                      {step.icon}
                    </div>
                    <div className="pt-2">
                      <h3 className="font-bold text-slate-900 mb-1">
                        {step.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-8"
          >
            <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 md:p-10 shadow-xl shadow-slate-200/40 relative overflow-hidden">
              <AnimatePresence>
                {isSuccess && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="absolute inset-0 z-20 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center text-center p-8 rounded-[2.5rem]"
                  >
                    <div className="w-24 h-24 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-inner">
                      <CheckCircle2 size={48} />
                    </div>
                    <h3 className="text-3xl font-black text-slate-900 mb-2">
                      {t.form.successTitle}
                    </h3>
                    <p className="text-slate-500 text-lg mb-8">
                      {t.form.successDesc}
                    </p>

                    {ticketNum && (
                      <div className="w-full max-w-sm bg-slate-50 border border-slate-200 rounded-3xl p-6 mb-8">
                        <p className="text-slate-500 font-bold mb-2">
                          {isRtl
                            ? "رقم التذكرة الخاص بك"
                            : "Your Ticket Number"}
                        </p>
                        <div className="text-xl font-black text-main tracking-widest uppercase">
                          {ticketNum}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm">
                      <Link
                        href={`/${locale}/track-complaint?ticket_number=${ticketNum}`}
                        className="flex-1 px-6 py-4 bg-slate-900 text-white font-bold rounded-2xl hover:bg-main transition-colors flex items-center justify-center gap-2"
                      >
                        {isRtl ? "تتبع حالة الطلب" : "Track Request Status"}
                      </Link>
                      <button
                        onClick={() => setIsSuccess(false)}
                        className="flex-1 px-6 py-4 bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 transition-colors"
                      >
                        {isRtl ? "إغلاق" : "Close"}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <h3 className="text-2xl font-black text-slate-900 mb-8">
                {t.form.title}
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      {t.form.fullName}
                    </label>
                    <input
                      {...register("fullName", { required: t.form.required })}
                      className={`w-full bg-slate-50 border rounded-2xl px-5 py-4 outline-none transition-all ${errors.fullName ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-main focus:bg-white"}`}
                      placeholder={isRtl ? "مثال: محمد" : "e.g., Mohammed"}
                    />
                    {errors.fullName && (
                      <p className="text-red-500 text-xs font-bold mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.fullName.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      {t.form.phone}
                    </label>
                    <input
                      {...register("phone", { required: t.form.required })}
                      className={`w-full bg-slate-50 border rounded-2xl px-5 py-4 outline-none transition-all ${errors.phone ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-main focus:bg-white"}`}
                      placeholder="05XXXXXXXX"
                      dir="ltr"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs font-bold mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.phone.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      {t.form.email}
                    </label>
                    <input
                      {...register("email", {
                        required: t.form.required,
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: t.form.invalidEmail,
                        },
                      })}
                      className={`w-full bg-slate-50 border rounded-2xl px-5 py-4 outline-none transition-all ${errors.email ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-main focus:bg-white"}`}
                      placeholder="name@example.com"
                      dir="ltr"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs font-bold mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      {t.form.type}
                    </label>
                    <select
                      {...register("type", { required: t.form.required })}
                      className={`w-full bg-slate-50 border rounded-2xl px-5 py-4 outline-none transition-all appearance-none cursor-pointer ${errors.type ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-main focus:bg-white"}`}
                    >
                      <option value="">
                        {isRtl ? "اختر نوع الطلب..." : "Select request type..."}
                      </option>
                      {t.form.types.map((type, i) => (
                        <option key={i} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-xs font-bold mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.type.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    {t.form.subject}
                  </label>
                  <input
                    {...register("subject", { required: t.form.required })}
                    className={`w-full bg-slate-50 border rounded-2xl px-5 py-4 outline-none transition-all ${errors.subject ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-main focus:bg-white"}`}
                    placeholder={
                      isRtl
                        ? "عنوان مختصر لطلبك"
                        : "Brief subject of your request"
                    }
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-xs font-bold mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.subject.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    {t.form.message}
                  </label>
                  <textarea
                    {...register("message", { required: t.form.required })}
                    rows={5}
                    className={`w-full bg-slate-50 border rounded-2xl px-5 py-4 outline-none transition-all resize-none ${errors.message ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-main focus:bg-white"}`}
                    placeholder={
                      isRtl
                        ? "اكتب تفاصيل شكواك أو اقتراحك هنا بوضوح..."
                        : "Write the details of your complaint or suggestion clearly here..."
                    }
                  />
                  {errors.message && (
                    <p className="text-red-500 text-xs font-bold mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.message.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full disabled:cursor-no-drop h-14 mt-4 bg-slate-900 text-white rounded-2xl font-black text-lg hover:bg-main hover:shadow-lg hover:shadow-main/30 transition-all disabled:opacity-70 flex items-center justify-center gap-2 group"
                >
                  {isPending ? (
                    <span className="animate-pulse">{t.form.submitting}</span>
                  ) : (
                    <>
                      <span>{t.form.submit}</span>
                      <Send
                        className={`w-5 h-5 transition-transform ${isRtl ? "group-hover:-translate-x-1 rotate-180" : "group-hover:translate-x-1"}`}
                      />
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
